import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { OperatorConversation } from '@/lib/operator-api/types';

// =============================================================================
// Conversation state options
// =============================================================================

const CONVERSATION_STATES = [
  'IDLE',
  'SELECTING_SERVICE',
  'SELECTING_DATE',
  'SELECTING_TIME',
  'CONFIRMING',
  'PAYING',
  'COMPLETED',
  'CANCELLED',
] as const;

// =============================================================================
// ConversationFilters
// =============================================================================

interface ConversationFiltersProps {
  phoneNumber: string;
  state: string;
  dateFrom: string;
  dateTo: string;
  onPhoneNumberChange: (value: string) => void;
  onStateChange: (value: string) => void;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onReset: () => void;
}

export function ConversationFilters({
  phoneNumber,
  state,
  dateFrom,
  dateTo,
  onPhoneNumberChange,
  onStateChange,
  onDateFromChange,
  onDateToChange,
  onReset,
}: ConversationFiltersProps) {
  const { t } = useTranslation();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {t('conversations.filters', 'Filters')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
          <div className="space-y-1.5">
            <Label htmlFor="conv-phone">
              {t('conversations.phoneNumber', 'Phone Number')}
            </Label>
            <Input
              id="conv-phone"
              type="text"
              placeholder={t('conversations.phoneNumberPlaceholder', 'Search by phone...')}
              value={phoneNumber}
              onChange={(e) => onPhoneNumberChange(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="conv-state">
              {t('conversations.state', 'State')}
            </Label>
            <Select
              value={state || '__all__'}
              onValueChange={(v) => onStateChange(v === '__all__' ? '' : v)}
            >
              <SelectTrigger id="conv-state">
                <SelectValue placeholder={t('conversations.allStates', 'All states')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">
                  {t('conversations.allStates', 'All states')}
                </SelectItem>
                {CONVERSATION_STATES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s.replace(/_/g, ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="conv-date-from">
              {t('conversations.dateFrom', 'Date From')}
            </Label>
            <Input
              id="conv-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => onDateFromChange(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="conv-date-to">
              {t('conversations.dateTo', 'Date To')}
            </Label>
            <Input
              id="conv-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => onDateToChange(e.target.value)}
            />
          </div>

          <div>
            <Button variant="outline" onClick={onReset} className="w-full">
              {t('common.reset', 'Reset')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// ConversationTable
// =============================================================================

interface ConversationTableProps {
  conversations: OperatorConversation[];
  loading: boolean;
  hasActiveFilters: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

function StepBadge({ step }: { step: string }) {
  const label = step.replace(/_/g, ' ');

  // Map steps to visual variants
  const variant: 'default' | 'secondary' | 'destructive' | 'outline' =
    step === 'COMPLETED'
      ? 'default'
      : step === 'CANCELLED'
        ? 'destructive'
        : step === 'PAYING' || step === 'CONFIRMING'
          ? 'secondary'
          : 'outline';

  return <Badge variant={variant}>{label}</Badge>;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function formatDateTime(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

export function ConversationTable({
  conversations,
  loading,
  hasActiveFilters,
  currentPage,
  totalPages,
  onPageChange,
}: ConversationTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </CardContent>
      </Card>
    );
  }

  if (conversations.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <p className="text-lg font-medium">
            {hasActiveFilters
              ? t('conversations.noResults', 'No conversations match your filters')
              : t('conversations.empty', 'No conversations yet')}
          </p>
          {hasActiveFilters && (
            <p className="text-sm mt-1">
              {t('conversations.tryAdjusting', 'Try adjusting your filter criteria')}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  const handleRowClick = (conversationId: string) => {
    navigate({ to: `/operator/conversations/${conversationId}` });
  };

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('conversations.customer', 'Customer')}</TableHead>
              <TableHead>{t('conversations.step', 'Status / Step')}</TableHead>
              <TableHead>{t('conversations.assignedTo', 'Assigned To')}</TableHead>
              <TableHead className="text-center">
                {t('conversations.messages', 'Messages')}
              </TableHead>
              <TableHead>{t('conversations.lastMessage', 'Last Message')}</TableHead>
              <TableHead>{t('conversations.created', 'Created')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {conversations.map((conv) => (
              <TableRow
                key={conv.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(conv.id)}
              >
                <TableCell>
                  <div>
                    <div className="font-medium">
                      {conv.customerName || t('conversations.unknown', 'Unknown')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {conv.customerPhone}
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <StepBadge step={conv.currentStep} />
                </TableCell>
                <TableCell>
                  {conv.assignedOperator ? (
                    <div>
                      <div className="text-sm font-medium">{conv.assignedOperator.name}</div>
                      <div className="text-xs text-muted-foreground">{conv.assignedOperator.email}</div>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{t('conversations.assignment.unassigned', 'Unassigned')}</span>
                  )}
                </TableCell>
                <TableCell className="text-center">
                  {conv.messageCount}
                </TableCell>
                <TableCell>
                  {formatDateTime(conv.lastMessageAt)}
                </TableCell>
                <TableCell>
                  {formatDate(conv.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <p className="text-sm text-muted-foreground">
            {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              {t('common.previous', 'Previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => onPageChange(currentPage + 1)}
            >
              {t('common.next', 'Next')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
